import { useState, useEffect, useRef } from 'react';
import { ChevronRight, CheckCircle2, XCircle, Loader2, ShieldCheck, Terminal, File, Search, Folder, Edit3, Trash2, Eye, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils.js';
import { useSchedulerStore } from '@/stores/schedulerStore.js';

interface ToolCallDisplayProps {
  toolName: string;
  toolArgs: Record<string, unknown>;
  toolResult?: unknown;
  success?: boolean;
  requireApproval?: boolean;
  approvalStatus?: 'pending' | 'approved' | 'rejected';
}

/**
 * Strip tool name prefixes and extract source
 * Handles: mcp__, mcp--, internal__, internal--, custom--
 */
function stripToolPrefix(toolName: string): { displayName: string; source: string } {
  if (toolName.startsWith('internal--')) {
    return { displayName: toolName.replace('internal--', ''), source: '' };
  }
  if (toolName.startsWith('custom--')) {
    return { displayName: toolName.replace('custom--', ''), source: '' };
  }
  if (toolName.startsWith('mcp--')) {
    const parts = toolName.split('--');
    if (parts.length >= 3) {
      return { displayName: parts.slice(2).join('--'), source: parts[1] ?? '' };
    }
    return { displayName: toolName.replace('mcp--', ''), source: 'mcp' };
  }
  if (toolName.startsWith('mcp__')) {
    const parts = toolName.substring(5).split('__');
    if (parts.length >= 2) {
      return { displayName: parts.slice(1).join('__'), source: parts[0] ?? '' };
    }
    return { displayName: toolName.substring(5), source: 'mcp' };
  }
  if (toolName.startsWith('internal__')) {
    return { displayName: toolName.substring(10), source: '' };
  }
  return { displayName: toolName, source: '' };
}

/**
 * Truncate text to a maximum length
 */
function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

/**
 * Get a shortened path (last 2 segments)
 */
function getShortPath(path: string): string {
  const parts = path.split('/').filter(Boolean);
  if (parts.length <= 2) return path;
  return '.../' + parts.slice(-2).join('/');
}

/**
 * Get an icon for the tool operation
 */
function getToolIcon(displayName: string): React.ReactNode {
  const name = displayName.toLowerCase();
  if (name.includes('bash') || name.includes('shell') || name.includes('exec')) {
    return <Terminal className="h-3 w-3" />;
  }
  if (name.includes('search') || name.includes('grep') || name.includes('find') || name.includes('glob')) {
    return <Search className="h-3 w-3" />;
  }
  if (name.includes('read') || name.includes('get') || name.includes('fetch') || name.includes('list')) {
    return <Eye className="h-3 w-3" />;
  }
  if (name.includes('write') || name.includes('create') || name.includes('add')) {
    return <File className="h-3 w-3" />;
  }
  if (name.includes('edit') || name.includes('update') || name.includes('modify')) {
    return <Edit3 className="h-3 w-3" />;
  }
  if (name.includes('delete') || name.includes('remove')) {
    return <Trash2 className="h-3 w-3" />;
  }
  if (name.includes('ask') || name.includes('prompt') || name.includes('question')) {
    return <MessageSquare className="h-3 w-3" />;
  }
  return <Folder className="h-3 w-3" />;
}

/**
 * Generate a user-friendly summary and detail for the tool call
 */
function getSummary(
  displayName: string,
  toolArgs: Record<string, unknown>
): { summary: string; detail?: string } {
  const args = toolArgs || {};

  // Extract common arg values
  const filePath = (args.file_path || args.path || args.file || args.filename) as string | undefined;
  const command = args.command as string | undefined;
  const pattern = (args.pattern || args.query || args.q || args.search) as string | undefined;
  const name = (args.name || args.title) as string | undefined;
  const url = args.url as string | undefined;

  // Handle shell/bash commands
  if (command) {
    return { summary: 'Ran command', detail: truncate(command, 50) };
  }

  // Handle search operations
  if (displayName.toLowerCase().includes('search') || displayName.toLowerCase().includes('grep')) {
    if (pattern && filePath) {
      return { summary: 'Searched', detail: `"${truncate(pattern, 20)}" in ${getShortPath(filePath)}` };
    }
    if (pattern) {
      return { summary: 'Searched', detail: `"${truncate(pattern, 30)}"` };
    }
  }

  // Handle glob/find operations
  if (displayName.toLowerCase().includes('glob') || displayName.toLowerCase().includes('find')) {
    if (pattern) {
      return { summary: 'Found files', detail: truncate(pattern, 40) };
    }
  }

  // Handle file operations with path
  if (filePath) {
    const shortPath = getShortPath(filePath);

    if (displayName.toLowerCase().includes('read') || displayName.toLowerCase().includes('get')) {
      return { summary: 'Read file', detail: shortPath };
    }
    if (displayName.toLowerCase().includes('write') || displayName.toLowerCase().includes('create')) {
      return { summary: 'Created file', detail: shortPath };
    }
    if (displayName.toLowerCase().includes('edit') || displayName.toLowerCase().includes('update') || displayName.toLowerCase().includes('modify')) {
      return { summary: 'Edited file', detail: shortPath };
    }
    if (displayName.toLowerCase().includes('delete') || displayName.toLowerCase().includes('remove')) {
      return { summary: 'Deleted file', detail: shortPath };
    }
    // Generic file operation
    return { summary: humanizeToolName(displayName), detail: shortPath };
  }

  // Handle URL operations
  if (url) {
    try {
      const urlObj = new URL(url);
      return { summary: humanizeToolName(displayName), detail: urlObj.hostname };
    } catch {
      return { summary: humanizeToolName(displayName), detail: truncate(url, 40) };
    }
  }

  // Handle named operations
  if (name) {
    return { summary: humanizeToolName(displayName), detail: truncate(name, 40) };
  }

  // Handle ask_user specifically
  if (displayName === 'ask_user' || displayName.includes('ask')) {
    return { summary: 'Asked user for input' };
  }

  // Handle list operations
  if (displayName.toLowerCase().includes('list')) {
    const resource = args.type || args.resource;
    if (resource && typeof resource === 'string') {
      return { summary: 'Listed', detail: resource };
    }
    return { summary: 'Listed items' };
  }

  // Fallback: humanize tool name
  return { summary: humanizeToolName(displayName) };
}

/**
 * Convert tool name to human-readable format
 */
function humanizeToolName(name: string): string {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase());
}

/**
 * Unwrap and parse tool result
 * Handles wrapped format: { content: [{ type: 'text', text: '...' }], meta: {...} }
 */
function unwrapToolResult(result: unknown): unknown {
  if (result === undefined || result === null) return result;

  // If it's a string, try to parse as JSON
  if (typeof result === 'string') {
    try {
      return unwrapToolResult(JSON.parse(result));
    } catch {
      return result;
    }
  }

  // If it's an object with content array (wrapped format)
  if (typeof result === 'object' && result !== null) {
    const obj = result as Record<string, unknown>;
    if (Array.isArray(obj.content) && obj.content.length > 0) {
      const firstContent = obj.content[0];
      if (typeof firstContent === 'object' && firstContent !== null) {
        const content = firstContent as Record<string, unknown>;
        if ('text' in content && typeof content.text === 'string') {
          try {
            return JSON.parse(content.text);
          } catch {
            return content.text;
          }
        }
        return firstContent;
      }
      if (typeof firstContent === 'string') {
        try {
          return JSON.parse(firstContent);
        } catch {
          return firstContent;
        }
      }
    }
  }

  return result;
}

/**
 * Format tool result for display
 */
function formatToolResult(result: unknown): { text: string; isLong: boolean } {
  const unwrapped = unwrapToolResult(result);

  if (unwrapped === undefined || unwrapped === null) {
    return { text: 'No result', isLong: false };
  }

  if (typeof unwrapped === 'string') {
    return { text: unwrapped, isLong: unwrapped.length > 500 };
  }

  const formatted = JSON.stringify(unwrapped, null, 2);
  return { text: formatted, isLong: formatted.length > 500 };
}

/**
 * Format tool args for display, filtering out very long values
 */
function formatToolArgs(args: Record<string, unknown>): Record<string, string> {
  const formatted: Record<string, string> = {};

  for (const [key, value] of Object.entries(args)) {
    if (value === undefined || value === null) continue;

    let stringValue: string;
    if (typeof value === 'string') {
      stringValue = value;
    } else if (typeof value === 'object') {
      stringValue = JSON.stringify(value);
    } else {
      stringValue = String(value);
    }

    // Truncate very long values but keep them
    if (stringValue.length > 200) {
      formatted[key] = stringValue.slice(0, 200) + '...';
    } else {
      formatted[key] = stringValue;
    }
  }

  return formatted;
}

export function ToolCallDisplay({
  toolName,
  toolArgs,
  toolResult,
  success,
  requireApproval = false,
  approvalStatus
}: ToolCallDisplayProps) {
  const [expanded, setExpanded] = useState(false);

  // Explicitly check if we have a result - if toolResult is defined (even if null), we're done processing
  const hasResult = toolResult !== undefined;
  const isProcessing = !hasResult;

  // Track if we've already processed this tool result to avoid duplicates
  const processedRef = useRef(false);

  // Handle scheduler tool results - update scheduler store directly
  useEffect(() => {
    // Only process once when we have a successful result
    if (!hasResult || !success || processedRef.current) {
      return;
    }

    // Check if this is a scheduler tool (with or without custom-- prefix)
    const normalizedName = toolName.replace('custom--', '');
    if (!normalizedName.includes('schedule')) {
      return;
    }

    // Mark as processed to avoid duplicate updates
    processedRef.current = true;

    // Parse result
    const unwrapped = unwrapToolResult(toolResult);
    if (!unwrapped || typeof unwrapped !== 'object') {
      return;
    }

    const result = unwrapped as Record<string, unknown>;
    const schedulerStore = useSchedulerStore.getState();

    switch (normalizedName) {
      case 'create_schedule': {
        const schedule = result.schedule as Record<string, unknown>;
        if (schedule) {
          schedulerStore.addSchedule(schedule as any);
        }
        break;
      }

      case 'update_schedule': {
        const schedule = result.schedule as Record<string, unknown>;
        if (schedule && schedule.id) {
          schedulerStore.updateSchedule(schedule.id as string, schedule as any);
        }
        break;
      }

      case 'delete_schedule': {
        const scheduleId = result.scheduleId as string;
        if (scheduleId) {
          schedulerStore.removeSchedule(scheduleId);
        }
        break;
      }

      default:
        // list_schedules, get_schedule, etc. don't need store updates
        break;
    }
  }, [toolName, toolResult, hasResult, success]);

  // Get display info
  const { displayName, source } = stripToolPrefix(toolName);
  const { summary, detail } = getSummary(displayName, toolArgs);
  const formattedArgs = formatToolArgs(toolArgs);
  const { text: resultText, isLong: isResultLong } = formatToolResult(toolResult);
  const icon = getToolIcon(displayName);

  return (
    <div className="flex gap-2.5 animate-slide-up my-1">
      {/* Timeline vertical line */}
      <div className="flex flex-col items-center">

        {/* Status indicator */}
        <div className="flex-shrink-0">
          {isProcessing ? (
            <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin" />
          ) : success ? (
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
          ) : (
            <XCircle className="h-3.5 w-3.5 text-red-500" />
          )}
        </div>

        {/* Vertical line continues below */}
        <div className={cn(
          "w-px flex-1",
          isProcessing ? "bg-blue-500/20" : success ? "bg-green-500/20" : "bg-red-500/20"
        )} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pb-1">
        {/* Summary line */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center gap-1.5 text-left group"
        >
          {/* Icon */}
          <span className={cn(
            "flex-shrink-0",
            isProcessing ? "text-blue-500" :
            success ? "text-muted-foreground/60" : "text-red-500"
          )}>
            {icon}
          </span>

          {/* Summary text */}
          <span className={cn(
            "text-xs font-medium",
            isProcessing ? "text-blue-600 dark:text-blue-400" :
            success ? "text-foreground/70" : "text-red-600 dark:text-red-400"
          )}>
            {summary}
          </span>

          {/* Detail text */}
          {detail && (
            <span className="text-xs text-muted-foreground/60 truncate max-w-[200px]">
              {detail}
            </span>
          )}

          {/* Approval indicator - show shield icon if tool required approval */}
          {requireApproval && approvalStatus === 'approved' && (
            <ShieldCheck
              className="h-3 w-3 text-green-600 dark:text-green-500 flex-shrink-0"
              aria-label="Required approval - Approved by user"
            />
          )}
          {requireApproval && approvalStatus === 'rejected' && (
            <ShieldCheck
              className="h-3 w-3 text-red-600 dark:text-red-500 flex-shrink-0"
              aria-label="Required approval - Rejected by user"
            />
          )}
          {requireApproval && approvalStatus === 'pending' && (
            <ShieldCheck
              className="h-3 w-3 text-yellow-600 dark:text-yellow-500 flex-shrink-0"
              aria-label="Required approval - Pending"
            />
          )}

          {source && (
            <span className="text-[10px] text-muted-foreground/40 flex-shrink-0">
              [{source}]
            </span>
          )}

          {hasResult && (
            <ChevronRight className={cn(
              "h-2.5 w-2.5 text-muted-foreground/40 transition-transform flex-shrink-0",
              expanded && "rotate-90"
            )} />
          )}

          {isProcessing && (
            <span className="text-[10px] text-muted-foreground/50">processing...</span>
          )}
        </button>

        {/* Tool name (subtle) */}
        <div className="text-[10px] text-muted-foreground/35 mt-0.5">
          {displayName}
        </div>

        {/* Expanded details */}
        {expanded && hasResult && (
          <div className="mt-2 space-y-2 animate-fade-in">
            {/* Tool arguments */}
            {Object.keys(formattedArgs).length > 0 && (
              <div>
                <h4 className="text-[9px] font-semibold text-muted-foreground/60 uppercase mb-1">Input</h4>
                <div className="bg-muted/30 rounded-md p-1.5 space-y-0.5 text-[10px]">
                  {Object.entries(formattedArgs).map(([key, value]) => (
                    <div key={key} className="flex gap-1.5">
                      <span className="text-muted-foreground font-medium shrink-0">{key}:</span>
                      <span className="text-foreground/70 font-mono break-all whitespace-pre-wrap">
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tool result */}
            <div>
              <h4 className="text-[9px] font-semibold text-muted-foreground/60 uppercase mb-1">Output</h4>
              <div className={cn(
                "bg-muted/30 rounded-md p-1.5",
                isResultLong && "max-h-48 overflow-y-auto"
              )}>
                <pre className="text-[10px] font-mono text-foreground/70 whitespace-pre-wrap break-all">
                  {resultText}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
